import { Alert, AlertDescription, AlertTitle } from '@repo/ui/alert'
import { Skeleton } from '@repo/ui/skeleton'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, stripSearchParams } from '@tanstack/react-router'
import { z } from 'zod'
import { useTranslation } from '@/lib/i18n'
import { ProvisionalRanking } from '../../../-components/provisional-ranking'
import { useWorkingCall } from '../../-hooks/use-working-call'
import { managedCallQueryOptions } from '../../-query/query-options'
import { ApproveRanking } from './-components/approve-ranking'
import { CalculationDetails } from './-components/calculation-details'
import { ConditionsCard } from './-components/conditions-card'
import { ControlLists } from './-components/control-lists'
import { RANKING_LISTS } from './-components/list-pages'
import {
	PublicationStatus,
	VersionLine
} from './-components/publication-status'
import { useRankingControl } from './-hooks/use-ranking-control'

const searchSchema = z.object({
	lista: z.enum(RANKING_LISTS).default('freshers').catch('freshers'),
	page: z.number().int().min(1).default(1).catch(1)
})

export const Route = createFileRoute(
	'/(private)/bando/$residenceId/graduatoria/'
)({
	component: RankingPage,
	validateSearch: searchSchema,
	search: {
		middlewares: [stripSearchParams(searchSchema.parse({}))]
	}
})

/**
 * The ranking of the residence as its manager checks it (FFD 2.7), read top
 * down: where it stands and what to do next (the conditions with the approval,
 * or the public page with the reports and the version in force), then one set
 * of tabs, the one shown in the address: the four lists, who is not admitted,
 * the applications left out. The calculation and the versions stay closed at
 * the foot.
 */
function RankingPage() {
	const { t } = useTranslation()
	const { residenceId } = Route.useParams()
	const query = useRankingControl()
	const { callId, readOnly } = useWorkingCall()
	const call = useQuery(managedCallQueryOptions(residenceId, callId)).data

	if (query.isLoading) {
		return (
			<div className='flex flex-col gap-4 p-4 md:p-6'>
				<Skeleton className='h-24 w-full' />
				<Skeleton className='h-48 w-full' />
			</div>
		)
	}
	if (query.isError || !query.data) {
		return (
			<div className='p-4 md:p-6'>
				<Alert variant='destructive'>
					<AlertTitle>{t('admin-ranking:page_title')}</AlertTitle>
					<AlertDescription>
						{t('admin-ranking:approve.errors.default')}
					</AlertDescription>
				</Alert>
			</div>
		)
	}
	const control = query.data.data
	const { ranking } = control
	const published = ranking !== null && ranking.state !== 'control'

	return (
		<div className='flex w-full flex-col gap-6 p-4 md:p-6'>
			{ranking && published && (
				<PublicationStatus
					openReports={control.openReports}
					publicPath={control.publicPath}
					publicUrl={call?.publicRankingUrl ?? null}
					ranking={ranking}
					residenceId={residenceId}
				/>
			)}
			{call?.state === 'open' && !ranking && (
				<ProvisionalRanking residenceId={residenceId} />
			)}
			{!published && call?.state !== 'open' && (
				<div className='flex flex-col gap-4'>
					{ranking && <VersionLine version={ranking.version} />}
					<ConditionsCard
						conditions={control.conditions}
						residenceId={residenceId}
					/>
					{ranking ? (
						!readOnly && (
							<ApproveRanking
								canApprove={control.canApprove}
								conditions={control.conditions}
								residenceId={residenceId}
								versionNumber={ranking.version.number}
							/>
						)
					) : (
						<p className='text-muted-foreground text-sm'>
							{t('admin-ranking:conditions.waits')}
						</p>
					)}
				</div>
			)}
			{ranking && (
				<>
					<ControlLists />
					<CalculationDetails
						version={ranking.version}
						versions={control.versions}
					/>
				</>
			)}
		</div>
	)
}
